import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import BudgetOverviewCards from './components/BudgetOverviewCards';
import BudgetTable from './components/BudgetTable';
import BudgetFilters from './components/BudgetFilters';
import VarianceAnalysisChart from './components/VarianceAnalysisChart';
import BudgetAllocationModal from './components/BudgetAllocationModal';
import BudgetAlertsPanel from './components/BudgetAlertsPanel';
import ForecastingPanel from './components/ForecastingPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const BudgetManagement = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    department: 'all',
    timePeriod: '2025',
    category: 'all'
  });
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [selectedBudgetItem, setSelectedBudgetItem] = useState(null);

  // Mock budget overview data
  const budgetOverviewData = [
    {
      id: 'total_budget',
      title: 'Total Budget',
      value: '$5.2M',
      allocated: '$4.8M',
      spent: '$3.2M',
      remaining: '$1.6M',
      utilizationRate: 67,
      change: '+8.2%',
      changeType: 'positive',
      icon: 'DollarSign'
    },
    {
      id: 'approved_budget',
      title: 'Approved Budget',
      value: '$4.8M',
      allocated: '$4.8M',
      spent: '$3.2M',
      remaining: '$1.6M',
      utilizationRate: 67,
      change: '+5.1%',
      changeType: 'positive',
      icon: 'CheckCircle'
    },
    {
      id: 'pending_approvals',
      title: 'Pending Approvals',
      value: '$340K',
      allocated: '$340K',
      spent: '$0',
      remaining: '$340K',
      utilizationRate: 0,
      change: '+12.5%',
      changeType: 'warning',
      icon: 'Clock'
    },
    {
      id: 'variance_alert',
      title: 'Budget Variance',
      value: '8.3%',
      allocated: '$4.8M',
      spent: '$3.2M',
      remaining: '$1.6M',
      utilizationRate: 67,
      change: '-2.1%',
      changeType: 'negative',
      icon: 'AlertTriangle'
    }
  ];

  // Mock budget table data
  const budgetTableData = [
    {
      id: 1,
      department: 'Sales',
      category: 'Marketing Campaigns',
      budgetCode: 'SALES-MKT-001',
      allocated: 850000,
      spent: 620000,
      remaining: 230000,
      utilizationRate: 72.9,
      status: 'on_track',
      lastUpdate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      approver: 'John Smith',
      notes: 'Q4 campaign budget performing well'
    },
    {
      id: 2,
      department: 'IT',
      category: 'Infrastructure',
      budgetCode: 'IT-INF-002',
      allocated: 1200000,
      spent: 1150000,
      remaining: 50000,
      utilizationRate: 95.8,
      status: 'over_budget',
      lastUpdate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      approver: 'Sarah Wilson',
      notes: 'Server upgrade exceeded initial estimates'
    },
    {
      id: 3,
      department: 'HR',
      category: 'Training & Development',
      budgetCode: 'HR-TRN-003',
      allocated: 450000,
      spent: 180000,
      remaining: 270000,
      utilizationRate: 40.0,
      status: 'under_utilized',
      lastUpdate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      approver: 'Michael Chen',
      notes: 'Training programs delayed due to scheduling'
    },
    {
      id: 4,
      department: 'Operations',
      category: 'Equipment & Supplies',
      budgetCode: 'OPS-EQP-004',
      allocated: 680000,
      spent: 475000,
      remaining: 205000,
      utilizationRate: 69.9,
      status: 'on_track',
      lastUpdate: new Date(Date.now() - 12 * 60 * 60 * 1000),
      approver: 'Lisa Anderson',
      notes: 'Regular operational expenses within limits'
    },
    {
      id: 5,
      department: 'Finance',
      category: 'Professional Services',
      budgetCode: 'FIN-SRV-005',
      allocated: 320000,
      spent: 298000,
      remaining: 22000,
      utilizationRate: 93.1,
      status: 'approaching_limit',
      lastUpdate: new Date(Date.now() - 6 * 60 * 60 * 1000),
      approver: 'David Brown',
      notes: 'Audit and consulting fees near limit'
    },
    {
      id: 6,
      department: 'R&D',
      category: 'Research Projects',
      budgetCode: 'RD-RSH-006',
      allocated: 920000,
      spent: 410000,
      remaining: 510000,
      utilizationRate: 44.6,
      status: 'under_utilized',
      lastUpdate: new Date(Date.now() - 18 * 60 * 60 * 1000),
      approver: 'Dr. Emily Johnson',
      notes: 'New product development phase 1 complete'
    }
  ];

  // Mock variance analysis data
  const varianceData = [
    { month: 'Jan', budgeted: 400000, actual: 380000, variance: -20000 },
    { month: 'Feb', budgeted: 420000, actual: 450000, variance: 30000 },
    { month: 'Mar', budgeted: 380000, actual: 375000, variance: -5000 },
    { month: 'Apr', budgeted: 460000, actual: 480000, variance: 20000 },
    { month: 'May', budgeted: 440000, actual: 425000, variance: -15000 },
    { month: 'Jun', budgeted: 400000, actual: 420000, variance: 20000 },
    { month: 'Jul', budgeted: 450000, actual: 465000, variance: 15000 },
    { month: 'Aug', budgeted: 430000, actual: 415000, variance: -15000 },
    { month: 'Sep', budgeted: 470000, actual: 485000, variance: 15000 },
    { month: 'Oct', budgeted: 490000, actual: 475000, variance: -15000 },
    { month: 'Nov', budgeted: 460000, actual: 490000, variance: 30000 },
    { month: 'Dec', budgeted: 480000, actual: 465000, variance: -15000 }
  ];

  // Mock alerts data
  const budgetAlertsData = [
    {
      id: 1,
      type: 'critical',
      category: 'over_budget',
      title: 'IT Infrastructure Budget Exceeded',
      message: 'IT Infrastructure budget has exceeded allocated amount by $150K. Immediate action required.',
      department: 'IT',
      budgetCode: 'IT-INF-002',
      amount: 150000,
      timestamp: new Date(Date.now() - 30 * 60 * 1000)
    },
    {
      id: 2,
      type: 'warning',
      category: 'approaching_limit',
      title: 'Finance Professional Services Approaching Limit',
      message: 'Finance Professional Services budget is 93% utilized with 2 months remaining.',
      department: 'Finance',
      budgetCode: 'FIN-SRV-005',
      amount: 22000,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
    },
    {
      id: 3,
      type: 'info',
      category: 'approval_pending',
      title: 'Marketing Campaign Budget Awaiting Approval',
      message: 'Q1 2026 Marketing campaign budget increase request pending executive approval.',
      department: 'Sales',
      budgetCode: 'SALES-MKT-007',
      amount: 75000,
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)
    },
    {
      id: 4,
      type: 'warning',
      category: 'under_utilized',
      title: 'HR Training Budget Under-utilized',
      message: 'HR Training & Development budget only 40% utilized. Consider reallocation.',
      department: 'HR',
      budgetCode: 'HR-TRN-003',
      amount: 270000,
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000)
    }
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFilterChange = (newFilters) => {
    setSelectedFilters(prev => ({ ...prev, ...newFilters }));
    console.log('Filters changed:', newFilters);
  };

  const handleBudgetItemAction = (action, item) => {
    if (action === 'edit') {
      setSelectedBudgetItem(item);
      setIsAllocationModalOpen(true);
    } else if (action === 'approve') {
      console.log('Approving budget item:', item);
    } else if (action === 'view_details') {
      console.log('Viewing details for:', item);
    }
  };

  const handleExportBudget = () => {
    console.log('Exporting budget data...');
    // Implement export functionality
  };

  const handleImportBudget = () => {
    console.log('Importing budget data...');
    // Implement import functionality
  };

  const handleCreateBudgetPlan = () => {
    setSelectedBudgetItem(null);
    setIsAllocationModalOpen(true);
  };

  useEffect(() => {
    // Simulate real-time budget updates
    const interval = setInterval(() => {
      console.log('Updating budget data...');
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={`transition-smooth pt-16 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="PieChart" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Budget Management</h1>
                  <p className="text-muted-foreground">Comprehensive budget planning, tracking, and variance analysis</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Upload" onClick={handleImportBudget}>
                Import
              </Button>
              <Button variant="outline" iconName="Download" onClick={handleExportBudget}>
                Export
              </Button>
              <Button variant="default" iconName="Plus" onClick={handleCreateBudgetPlan}>
                New Budget Plan
              </Button>
            </div>
          </div>

          {/* Budget Overview Cards */}
          <BudgetOverviewCards data={budgetOverviewData} />

          {/* Filters */}
          <BudgetFilters 
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Budget Table - Takes up 3 columns */}
            <div className="xl:col-span-3 space-y-6">
              <BudgetTable 
                data={budgetTableData}
                onItemAction={handleBudgetItemAction}
              />
              <VarianceAnalysisChart data={varianceData} />
            </div>

            {/* Right Sidebar - Takes up 1 column */}
            <div className="space-y-6">
              <BudgetAlertsPanel alerts={budgetAlertsData} />
              <ForecastingPanel />
            </div>
          </div>

          {/* Real-time Status Indicator */}
          <div className="fixed bottom-6 right-6 z-50">
            <div className="bg-card border border-border rounded-full px-4 py-2 card-shadow flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-xs text-muted-foreground">Budget Data Live</span>
            </div>
          </div>
        </div>
      </main>

      {/* Budget Allocation Modal */}
      {isAllocationModalOpen && (
        <BudgetAllocationModal
          budgetItem={selectedBudgetItem}
          isOpen={isAllocationModalOpen}
          onClose={() => setIsAllocationModalOpen(false)}
          onSave={(data) => {
            console.log('Saving budget allocation:', data);
            setIsAllocationModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default BudgetManagement;